/**
 * Shared QueryClient with global mutation toasts.
 *
 * Opt-in success:  meta: { successMessage: "Allergy saved" }
 * Opt-out errors:  meta: { silent: true } | { skipErrorToast: true }
 * Opt-out success: meta: { silentSuccess: true }
 *
 * Mutations with their own onError handler skip the global error toast automatically.
 */
import { MutationCache, QueryClient } from "@tanstack/react-query";
import { getErrorMessage } from "@/lib/user-messages";
import { toast } from "@/lib/toast";

function mutationHasCustomHandler(
  mutation: { options: { onError?: unknown; onSuccess?: unknown } },
  key: "onError" | "onSuccess",
): boolean {
  return typeof mutation.options[key] === "function";
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _context, mutation) => {
        const meta = mutation.meta as Record<string, unknown> | undefined;
        if (meta?.silent || meta?.silentSuccess) return;

        const message = meta?.successMessage;
        if (typeof message === "string" && message.length > 0) {
          toast({ variant: "success", title: message });
        }
      },
      onError: (error, _variables, _context, mutation) => {
        const meta = mutation.meta as Record<string, unknown> | undefined;
        if (meta?.silent || meta?.skipErrorToast) return;
        // Custom onError handlers (toast, rollback, inline form errors) own the UX.
        // Opt back in with meta.allowGlobalErrorToast when onError only rolls back cache.
        if (mutationHasCustomHandler(mutation, "onError") && !meta?.allowGlobalErrorToast) {
          return;
        }

        const title =
          typeof meta?.errorTitle === "string"
            ? meta.errorTitle
            : getErrorMessage(error, "general");
        // Safety net only: yields to any error toast the screen shows itself
        // (e.g. a mutate(..., { onError }) or try/catch around mutateAsync).
        toast({ variant: "destructive", title, fallback: true });
      },
    }),
  });
}

/** Helpers for mutation meta - use in useMutation({ meta: saved("Patient") }) */
export const mutationMeta = {
  saved: (entity = "Changes") => ({ successMessage: `${entity} saved` }),
  created: (entity = "Record") => ({ successMessage: `${entity} created` }),
  updated: (entity = "Record") => ({ successMessage: `${entity} updated` }),
  deleted: (entity = "Record") => ({ successMessage: `${entity} deleted` }),
  signed: (entity = "Note") => ({ successMessage: `${entity} signed` }),
  sent: (entity = "Message") => ({ successMessage: `${entity} sent` }),
  submitted: (entity = "Claim") => ({ successMessage: `${entity} submitted` }),
  cancelled: (entity = "Appointment") => ({ successMessage: `${entity} cancelled` }),
  checkedIn: { successMessage: "Patient checked in" },
  silent: { silent: true },
  silentSuccess: { silentSuccess: true },
  /** Use with optimistic onError rollback when you still want the global error toast. */
  allowGlobalErrorToast: { allowGlobalErrorToast: true },
} as const;
